/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { getConfig } from "../config";

const useJWTTokenManager = (tokens, setTokens, handleLogout) => {
  const [showExpiryDialog, setShowExpiryDialog] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(30);
  const [isRefreshingToken, setIsRefreshingToken] = useState(false);

  // Refs for intervals
  const countdownIntervalRef = useRef(null);
  const tokenCheckIntervalRef = useRef(null);

  const cognitoDomain = getConfig().REACT_APP_COGNITO_DOMAIN;
  const app_portal = getConfig().REACT_APP_PORTAL;

  const clientId = useMemo(() => {
    const config = getConfig();
    const clientIdMap = {
      "oem-admin": config.REACT_APP_OEM_ADMIN_CLIENT_ID,
      "seller": config.REACT_APP_CLIENT_ID,
      "domain-approver": config.REACT_APP_DOMAIN_APPROVER_CLIENT_ID,
      "global-admin": config.REACT_APP_GLOBAL_ADMIN_CLIENT_ID,
    };
    return clientIdMap[app_portal] || "";
  }, [app_portal]);

  const vw_client_id = useMemo(() => {
    const config = getConfig();
    const vwClientIdMap = {
      "oem-admin": config.REACT_APP_VW_OEM_ADMIN_CLIENT_ID,
      "seller": config.REACT_APP_VW_CLIENT_ID,
      "domain-approver": config.REACT_APP_VW_DOMAIN_APPROVER_CLIENT_ID,
    };
    return vwClientIdMap[app_portal] || "";
  }, [app_portal]);

  // Check if token is valid
  const isTokenValid = useCallback(() => {
    const accessToken = sessionStorage.getItem("access_token");

    if (!accessToken) {
      return false;
    }

    try {
      const decodedToken = jwtDecode(accessToken);
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if token is expired (with 5 minute buffer)
      const isValid = decodedToken.exp > currentTime + 300;
      return isValid;
    } catch {
      return false;
    }
  }, []);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    const refreshTokenValue = sessionStorage.getItem("refresh_token");

    if (!refreshTokenValue) {
      handleLogout();
      return false;
    }

    setIsRefreshingToken(true);

    try {
      const tokenUrl = `${cognitoDomain}/oauth2/token`;
      const body = new URLSearchParams({
        grant_type: "refresh_token",
        client_id:
          sessionStorage.getItem("idpLogin") === "true"
            ? vw_client_id
            : clientId,
        refresh_token: refreshTokenValue,
      });

      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const newTokens = await response.json();

      if (newTokens.access_token) {
        // Update session storage with new tokens
        sessionStorage.setItem("access_token", newTokens.access_token);
        sessionStorage.setItem("id_token", newTokens.id_token);
        if (newTokens.refresh_token) {
          sessionStorage.setItem("refresh_token", newTokens.refresh_token);
        }
        sessionStorage.setItem("expires_in", newTokens.expires_in);

        // Update state
        setTokens({
          access_token: newTokens.access_token,
          expires_in: newTokens.expires_in,
          id_token: newTokens.id_token,
          refresh_token: newTokens.refresh_token || refreshTokenValue,
          token_type: "Bearer",
        });

        setIsRefreshingToken(false);
        return true;
      } else {
        setIsRefreshingToken(false);
        handleLogout();
        return false;
      }
    } catch {
      setIsRefreshingToken(false);
      handleLogout();
      return false;
    }
  }, [cognitoDomain, vw_client_id, clientId, setTokens, handleLogout]);

  // Show expiry dialog with countdown
  const showExpiryDialogWithCountdown = useCallback(
    (timeLeft) => {
      setRemainingSeconds(timeLeft);
      setShowExpiryDialog(true);

      // Clear any existing countdown interval
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      // Start countdown interval
      countdownIntervalRef.current = setInterval(() => {
        setRemainingSeconds((prevSeconds) => {
          if (prevSeconds <= 1) {
            // Time's up - logout

            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
            }
            setShowExpiryDialog(false);
            handleLogout();
            return 0;
          }
          return prevSeconds - 1;
        });
      }, 1000);
    },
    [handleLogout]
  );

  // Handle continue button click
  const handleContinueSession = useCallback(async () => {
    // Clear countdown interval immediately
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    // Close dialog immediately when continue is clicked
    setShowExpiryDialog(false);

    // Call refresh token function
    await refreshToken();
  }, [refreshToken]);

  // Handle logout from dialog
  const handleLogoutFromDialog = useCallback(() => {
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setShowExpiryDialog(false);
    handleLogout();
  }, [handleLogout]);

  // JWT Token Expiry Monitoring
  useEffect(() => {
    const checkTokenExpiry = () => {
      const accessToken = sessionStorage.getItem("access_token");

      if (!accessToken) {
        return;
      }

      try {
        const decodedToken = jwtDecode(accessToken);
        const currentTime = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = decodedToken.exp - currentTime;

        // Console log to track remaining time every 10 seconds

        // Show dialog when 30 seconds or less remaining
        if (timeUntilExpiry <= 30 && timeUntilExpiry > 0 && !showExpiryDialog) {
          showExpiryDialogWithCountdown(timeUntilExpiry);
        }

        // If token is already expired, logout immediately
        if (timeUntilExpiry <= 0) {
          handleLogout();
        }
      } catch (error) {
        console.error("Error checking JWT token expiry:", error);
        handleLogout();
      }
    };

    if (tokens.access_token || sessionStorage.getItem("access_token")) {
      // Start periodic checking every 10 seconds
      tokenCheckIntervalRef.current = setInterval(checkTokenExpiry, 10000);

      // Check immediately on mount
      checkTokenExpiry();
    }

    // Cleanup function when component unmounts or tokens change
    return () => {
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
        tokenCheckIntervalRef.current = null;
      }
    };
  }, [
    tokens.access_token,
    showExpiryDialog,
    showExpiryDialogWithCountdown,
    handleLogout,
  ]);

  // Cleanup intervals on component unmount
  useEffect(() => {
    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
      }
    };
  }, []);

  return {
    showExpiryDialog,
    remainingSeconds,
    isRefreshingToken,
    isTokenValid,
    handleContinueSession,
    handleLogoutFromDialog,
    showExpiryDialogWithCountdown,
    setShowExpiryDialog,
  };
};

export default useJWTTokenManager;

